import React, { useState, useMemo, useEffect, useRef } from 'react';
import { db } from '../../../firebase/config';
import { 
    doc, 
    getDoc, 
    onSnapshot, 
    collection, 
    writeBatch, 
    serverTimestamp, 
    updateDoc
} from 'firebase/firestore'; 
import { pdf } from '@react-pdf/renderer'; // Generación directa de PDF en memoria
import ModalEdicionContrato from '../../ui/ModalEdicionContrato';
import ModalNotificacion from '../../ui/ModalNotificacion'; 
import PlantillaCotizacionArtPDF from '../TablerosGestion/ART/PlantillaPdf/PlantillaCotizacionArtPDF';

// --- HELPERS DE FORMATO ---
const formatCurrency = (val) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(val || 0);
const formatPercent = (val) => `${Number(val || 0).toFixed(2).replace('.', ',')}%`;

// --- ICONOS ---
const IconArrowLeft = () => <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>;
const IconDownload = () => <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>;
const IconTrophy = ({ className }) => <svg className={className || "w-5 h-5"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/></svg>;
const IconDollarSign = () => <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>;
const IconCalendar = () => <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
const IconBuilding = () => <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/><path d="M10 6h4"/><path d="M10 10h4"/><path d="M10 14h4"/><path d="M10 18h4"/></svg>;
const IconBriefcase = () => <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="14" x="2" y="7" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>;
const IconUserPlus = () => <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>;
const IconCheck = () => <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>;
const IconMail = () => <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>;
const IconPhone = () => <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81 0.7A2 2 0 0 1 22 16.92z"/></svg>;
const IconInfo = () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;

const DetalleCotizacionArt = ({ cotizacion: initialCotizacion, onBack }) => { 
    // Bypass de permisos de usuario temporal para habilitar todas las acciones comerciales
    const isFullUser = true; 
    
    const [cotizacion, setCotizacion] = useState(initialCotizacion);
    const currentPathRef = useRef(initialCotizacion?.path);

    useEffect(() => {
        if (initialCotizacion) {
            setCotizacion(initialCotizacion);
            currentPathRef.current = initialCotizacion.path;
        }
    }, [initialCotizacion]);

    const [isGenerating, setIsGenerating] = useState(false);
    const [fetchedClient, setFetchedClient] = useState(null);

    const [showContratoModal, setShowContratoModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false); 
    const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
    
    const [contratoPrefill, setContratoPrefill] = useState(null);
    const [clientDataForModal, setClientDataForModal] = useState(null);
    const [selectedComparacion, setSelectedComparacion] = useState(null);

    // --- 1. SINCRONIZACIÓN EN TIEMPO REAL ---
    useEffect(() => {
        if (!currentPathRef.current) return;
        
        const unsubscribe = onSnapshot(doc(db, currentPathRef.current), (snap) => {
            if (snap.exists()) {
                const data = { id: snap.id, path: snap.ref.path, ...snap.data() };
                setCotizacion(data);
            }
        }, (err) => console.error("Error en snapshot detalle:", err));
        
        return () => unsubscribe();
    }, [cotizacion?.path]);

    // --- 2. DATOS DE IDENTIDAD DEL CLIENTE ---
    useEffect(() => {
        const fetchClientData = async () => {
            const targetClientId = cotizacion?.clienteId || cotizacion?.entidadId;
            if (!targetClientId) return;

            try {
                const clientDoc = await getDoc(doc(db, 'public/data/clientes', targetClientId));
                if (clientDoc.exists()) {
                    setFetchedClient({ id: clientDoc.id, ...clientDoc.data(), tipoEntidad: 'CLIENTE' });
                    return;
                }
                const prospectDoc = await getDoc(doc(db, 'public/data/prospectos', targetClientId));
                if (prospectDoc.exists()) {
                    setFetchedClient({ id: prospectDoc.id, ...prospectDoc.data(), tipoEntidad: 'PROSPECTO' });
                }
            } catch (error) { console.error("Error fetching client data:", error); }
        };
        fetchClientData();
    }, [cotizacion?.clienteId, cotizacion?.entidadId]);

    const sourceData = fetchedClient || cotizacion;
    
    const fullName = useMemo(() => {
        const nom = sourceData.clienteNombre || sourceData.nombre || sourceData.entidadNombre || '';
        const ape = sourceData.clienteApellido || sourceData.apellido || '';
        const res = `${nom} ${ape}`.trim();
        return res === '' ? (fetchedClient ? 'SIN NOMBRE' : 'Cargando...') : res;
    }, [sourceData, fetchedClient]);

    const displayEmail = useMemo(() => {
        if (Array.isArray(sourceData.emails) && sourceData.emails.length > 0) {
            const first = sourceData.emails[0];
            return (typeof first === 'object' ? (first.valor || first.email) : first) || 'No informado';
        }
        return sourceData.email || sourceData.clienteEmail || sourceData.entidadEmail || 'No informado';
    }, [sourceData]);

    const displayPhone = useMemo(() => {
        if (Array.isArray(sourceData.telefonos) && sourceData.telefonos.length > 0) {
            const first = sourceData.telefonos[0];
            return (typeof first === 'object' ? (first.valor || first.telefono) : first) || 'No informado';
        }
        return sourceData.telefono || sourceData.clienteTelefono || sourceData.entidadPhone || 'No informado';
    }, [sourceData]);

    const clientCuit = (sourceData.clienteCuit || sourceData.cuit || sourceData.entidadCuit || '-').toString().trim();
    const isProspect = (cotizacion.tipoEntidad || fetchedClient?.tipoEntidad) === 'PROSPECTO';
    
    const isContratoGenerado = useMemo(() => {
        return !!(cotizacion.contratoVinculadoId || cotizacion.nContratoVinculado || cotizacion.estado === 'Cerrada Ganada');
    }, [cotizacion.contratoVinculadoId, cotizacion.nContratoVinculado, cotizacion.estado]);

    // --- 3. PROCESAMIENTO DE OFERTAS ---
    const { ofertas, listaVisual } = useMemo(() => {
        if (!cotizacion || !cotizacion.cotizaciones) return { ofertas: [], listaVisual: [] };
        const validas = [];
        const invalidas = [];
        cotizacion.cotizaciones.forEach(c => {
            const estado = (c.estadoCotizacion || c.estadoRow || '').trim();
            const alicuota = parseFloat(c.alicuotaCotizada);
            if (alicuota > 0 && !['Solicitada', 'Bloqueada', 'No cotiza', 'Pendiente', 'Stand By'].includes(estado)) {
                validas.push(c);
            } else {
                invalidas.push(c);
            }
        });
        validas.sort((a, b) => parseFloat(a.alicuotaCotizada) - parseFloat(b.alicuotaCotizada));
        return { ofertas: validas, listaVisual: [...validas, ...invalidas] };
    }, [cotizacion?.cotizaciones]);

    const ganadora = useMemo(() => (ofertas.length > 0 ? ofertas[0] : null), [ofertas]);

    useEffect(() => {
        if (ganadora && !selectedComparacion) setSelectedComparacion(ganadora);
    }, [ganadora, selectedComparacion]);

    const ofertaParaCalculos = selectedComparacion || ganadora;

    const masaSalarial = parseFloat(cotizacion?.masaSalarial || 0);
    const empleados = parseInt(cotizacion?.capitas || cotizacion?.cantEmpleados || 1, 10);
    const alicuotaActual = parseFloat(cotizacion?.alicuotaActual || 0); 
    const costoMensualActual = (masaSalarial * (alicuotaActual / 100));

    const stats = useMemo(() => {
        const oferta = ofertaParaCalculos;
        if (!oferta || parseFloat(oferta.alicuotaCotizada) <= 0) return { costoMensual: 0, costoPorCapita: 0, diferenciaMensual: 0, diferenciaAnual: 0, diferenciaPorcentual: 0 };
        const alicuota = parseFloat(oferta.alicuotaCotizada);
        const costoMensual = (masaSalarial * (alicuota / 100));
        const costoPorCapita = empleados > 0 ? costoMensual / empleados : 0;
        const diferenciaMensual = costoMensual - costoMensualActual;
        const diferenciaAnual = diferenciaMensual * 13;
        const diferenciaPorcentual = costoMensualActual > 0 ? ((costoMensual - costoMensualActual) / costoMensualActual) * 100 : 0;
        return { alicuota, costoMensual, costoPorCapita, diferenciaMensual, diferenciaAnual, diferenciaPorcentual };
    }, [ofertaParaCalculos, masaSalarial, empleados, costoMensualActual]);

    // --- ACCIÓN: CONVERTIR PROSPECTO A CLIENTE ---
    const handleConvertToClient = async () => {
        if (!fetchedClient || fetchedClient.tipoEntidad !== 'PROSPECTO') return;
        setIsGenerating(true);
        setShowConfirmModal(false);
        try {
            const batch = writeBatch(db);
            const newClientRef = doc(collection(db, 'public/data/clientes'));
            const prospectRef = doc(db, 'public/data/prospectos', fetchedClient.id);
            
            const { id, tipoEntidad, telefono, email, ...restOfData } = fetchedClient;
            const clientData = { 
                ...restOfData, 
                telefonos: telefono ? [String(telefono)] : (fetchedClient.telefonos || []), 
                emails: email ? [String(email)] : (fetchedClient.emails || []),
                ultimaModificacion: new Date().toISOString()
            };

            batch.set(newClientRef, clientData);
            batch.delete(prospectRef);
            
            const oldPath = currentPathRef.current;
            const newPath = `public/data/clientes/${newClientRef.id}/cotizaciones_art/${cotizacion.id}`;
            
            const quotePayload = {
                ...cotizacion,
                clienteId: newClientRef.id,
                tipoEntidad: 'CLIENTE',
                ultimaModificacion: new Date().toISOString()
            };
            
            const { id: _, path: __, ...cleanedQuote } = quotePayload;

            batch.set(doc(db, newPath), cleanedQuote);
            batch.delete(doc(db, oldPath));

            await batch.commit();

            currentPathRef.current = newPath;
            setCotizacion({ ...quotePayload, id: cotizacion.id, path: newPath });
            setFetchedClient({ ...clientData, id: newClientRef.id, tipoEntidad: 'CLIENTE' });
            
            setNotification({ show: true, message: 'Prospecto convertido y cotización trasladada con éxito', type: 'success' });
        } catch (error) {
            console.error("Error al convertir prospecto:", error);
            setNotification({ show: true, message: 'Error al procesar la conversión', type: 'error' });
        } finally {
            setIsGenerating(false);
        }
    };

    // --- ACCIÓN: VINCULACIÓN DE CONTRATO ---
    const handleContractSuccess = async (msg, contractData) => {
        setShowContratoModal(false);
        const activePath = currentPathRef.current;
        
        if (!activePath) {
            console.error("Path de cotización no encontrado");
            return;
        }

        try {
            const cotRef = doc(db, activePath);
            
            const updatePayload = {
                estado: 'Cerrada Ganada',
                nContratoVinculado: contractData.nContratoVinculado || contractData.nContrato || 'S/N',
                contratoVinculadoId: contractData.contratoVinculadoId || contractData.id || '',
                ultimaModificacion: new Date().toISOString()
            };

            await updateDoc(cotRef, updatePayload);
            setCotizacion(prev => ({ ...prev, ...updatePayload }));

            setNotification({ 
                show: true, 
                message: `¡Contrato N° ${updatePayload.nContratoVinculado} emitido y vinculado correctamente!`, 
                type: 'success' 
            });
        } catch (e) {
            console.error("Error crítico vinculando contrato:", e);
            setNotification({ 
                show: true, 
                message: 'El contrato se emitió pero la vinculación automática falló. Por favor, edita la cotización manualmente para cerrarla.', 
                type: 'error' 
            });
        }
    };

    const handleOpenContractModal = () => {
        if (isContratoGenerado || isProspect) return;
        
        setContratoPrefill({
            ciiu: cotizacion.ciiu || sourceData.ciiu || '',
            capitas: cotizacion.capitas || '',
            masaSalarial: cotizacion.masaSalarial || '',
            alicuota: ofertaParaCalculos?.alicuotaCotizada || '', 
            compania: ofertaParaCalculos?.compania || '', 
            vendedor: cotizacion.vendedor || sourceData.vendedor || '',
            canal: sourceData.canal || cotizacion.canal || '',
            canalNombreId: sourceData.canalNombreId || cotizacion.canalNombreId || '',
            canalNombre: sourceData.canalNombre || cotizacion.canalNombre || '',
            subcanalNombreId: sourceData.subcanalNombreId || cotizacion.subcanalNombreId || '',
            subcanalNombre: sourceData.subcanalNombre || cotizacion.subcanalNombre || '',
            tipoConversion: 'Cliente',
            cotizacionId: cotizacion.id, 
            cotizacionPath: currentPathRef.current 
        });

        setClientDataForModal({
            ...sourceData,
            id: fetchedClient?.id || cotizacion.clienteId || cotizacion.entidadId,
            cuit: clientCuit,
            vendedor: cotizacion.vendedor || sourceData.vendedor || ''
        });
        setShowContratoModal(true);
    };

    const handleDownloadPdf = async () => {
        setIsGenerating(true);
        try {
            const blob = await pdf(
                <PlantillaCotizacionArtPDF 
                    cotizacion={cotizacion} 
                    oferta={ofertaParaCalculos} // Sincroniza de forma estricta la oferta seleccionada
                    stats={stats} 
                    fetchedClient={fetchedClient}
                    clientCuit={clientCuit}
                    formatCurrency={formatCurrency} 
                    formatPercent={formatPercent} 
                />
            ).toBlob();

            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Cotizacion_ART_${fullName.replace(/\s+/g, '_')}.pdf`;
            
            link.click();
            URL.revokeObjectURL(url);
            
            setNotification({ show: true, message: 'PDF oficial vectorial generado con éxito', type: 'success' });
        } catch (error) {
            console.error("Error crítico al exportar el PDF vectorial:", error);
            setNotification({ show: true, message: 'Error al generar el presupuesto en PDF', type: 'error' });
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <div className="animate-fade-in p-6 md:p-10 pb-20 text-left bg-white min-h-screen relative overflow-y-auto max-h-screen">
            <div className="max-w-[1600px] mx-auto space-y-6">
                
                {/* BANNER DE VINCULACIÓN EXITOSA */}
                {isContratoGenerado && (
                    <div className="bg-blue-600 text-white px-6 py-4 rounded-xl shadow-lg flex items-center justify-between animate-bounce-in border-b-4 border-blue-800">
                        <div className="flex items-center gap-4">
                            <div className="bg-white/20 p-2 rounded-lg"><IconInfo /></div>
                            <div>
                                <p className="font-bold text-lg leading-none">Contrato Vinculado</p>
                                <p className="text-blue-100 text-sm mt-1 italic">N° de Contrato: <span className="font-mono bg-blue-700 px-2 py-0.5 rounded font-bold text-white tracking-wider">{cotizacion.nContratoVinculado || '---'}</span></p>
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-white p-4 rounded-xl border border-slate-200 shadow-sm sticky top-0 z-10 gap-4">
                    <div className="flex items-center gap-4">
                        <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-500"><IconArrowLeft /></button>
                        <div>
                            <div className="flex items-center gap-2">
                                <h1 className="text-xl font-bold text-slate-800 uppercase">{fullName}</h1>
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded border uppercase ${isProspect ? 'bg-orange-50 text-orange-600 border-orange-100' : 'bg-blue-50 text-blue-600 border-blue-100'}`}>{isProspect ? 'PROSPECTO' : 'CLIENTE'}</span>
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                                <div className="flex items-center gap-1"><IconMail /> {displayEmail}</div>
                                <div className="flex items-center gap-1 border-l border-slate-200 pl-3"><IconPhone /> {displayPhone}</div>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 w-full md:w-auto h-[42px]">
                        {isFullUser && (
                            <>
                                {isProspect && !isContratoGenerado ? (
                                    <button 
                                        onClick={() => setShowConfirmModal(true)}
                                        disabled={isGenerating}
                                        className="flex-1 md:flex-none flex items-center justify-center gap-2 bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 font-medium text-sm shadow-md transition-all h-full whitespace-nowrap active:scale-95"
                                    >
                                        <IconUserPlus /> Convertir a Cliente
                                    </button>
                                ) : !isProspect && (
                                    <button 
                                        disabled={true}
                                        className="flex-1 md:flex-none flex items-center justify-center gap-2 bg-slate-50 text-slate-500 px-4 py-2 rounded-lg border border-slate-200 font-medium text-sm h-full whitespace-nowrap"
                                    >
                                        <IconCheck /> Cliente Registrado
                                    </button>
                                )}

                                <div className="relative group flex-1 md:flex-none h-full">
                                    <button 
                                        onClick={handleOpenContractModal} 
                                        disabled={isContratoGenerado || isProspect} 
                                        className={`w-full h-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium text-sm shadow-sm transition-all whitespace-nowrap ${
                                            (isContratoGenerado || isProspect) 
                                                ? 'bg-slate-50 text-slate-400 cursor-not-allowed border-slate-100 shadow-none' 
                                                : 'bg-blue-600 text-white hover:bg-blue-700 active:scale-95'
                                        }`}
                                    >
                                        <IconBriefcase /> {isContratoGenerado ? 'Contrato Emitido' : 'Generar Contrato'}
                                    </button>
                                    {isProspect && !isContratoGenerado && (
                                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block w-48 bg-gray-800 text-white text-[10px] p-2 rounded shadow-xl text-center z-50 animate-fade-in font-bold uppercase tracking-tighter">
                                            Primero debe convertir el prospecto a cliente para habilitar esta función.
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                        <button onClick={handleDownloadPdf} disabled={isGenerating} className="flex-1 md:flex-none flex items-center justify-center gap-2 bg-slate-800 text-white px-4 py-2 rounded-lg hover:bg-slate-700 font-medium text-sm shadow-md transition-all active:scale-95 h-full whitespace-nowrap"><IconDownload /> {isGenerating ? 'Generando...' : 'Descargar PDF'}</button>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="space-y-6">
                        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                            <h3 className="text-sm font-bold text-slate-400 uppercase mb-4 flex items-center gap-2"><IconBuilding /> Datos de Empresa</h3>
                            <div className="space-y-3 text-sm">
                                <div className="flex justify-between border-b border-slate-50 pb-2"><span className="text-slate-500">Empleados</span><span className="font-semibold text-slate-800">{empleados}</span></div>
                                <div className="flex justify-between border-b border-slate-50 pb-2"><span className="text-slate-500">Masa Salarial</span><span className="font-semibold text-slate-800">{formatCurrency(masaSalarial)}</span></div>
                                <div className="flex justify-between border-b border-slate-50 pb-2"><span className="text-slate-500">CIIU</span><span className="font-semibold text-slate-800">{cotizacion.ciiu || '-'}</span></div>
                                <div className="flex justify-between border-b border-slate-50 pb-2"><span className="text-slate-500">Alic. Actual (%)</span><span className="font-semibold text-slate-800">{formatPercent(alicuotaActual)}</span></div>
                                <div className="flex justify-between pt-2"><span className="text-slate-500">ART Actual</span><span className="font-semibold text-blue-600 uppercase">{cotizacion.artActual || 'No informada'}</span></div>
                            </div>
                        </div>

                        {ofertaParaCalculos && (
                            <div className="bg-gradient-to-br from-slate-900 to-slate-800 p-6 rounded-xl shadow-lg text-white">
                                <h3 className="text-lg font-bold mb-6 italic">Análisis de Ahorro</h3>
                                <div className="grid grid-cols-2 gap-4 mb-6">
                                    <div className="bg-white/10 p-3 rounded-lg backdrop-blur-sm">
                                        <p className="text-slate-300 text-[10px] uppercase font-bold mb-1">Costo Nuevo</p>
                                        <p className="text-lg font-bold text-emerald-400">{formatCurrency(stats.costoMensual)}</p>
                                    </div>
                                    <div className="bg-white/5 p-3 rounded-lg">
                                        <p className="text-slate-400 text-[10px] uppercase font-bold mb-1">Costo Actual</p>
                                        <p className="text-lg font-bold text-slate-200">{formatCurrency(costoMensualActual)}</p>
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${stats.diferenciaMensual < 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}><IconDollarSign /></div>
                                            <span className="text-sm font-medium">Diferencia Mensual</span>
                                        </div>
                                        <span className={`font-bold ${stats.diferenciaMensual < 0 ? 'text-emerald-400' : 'text-red-400'}`}>{formatCurrency(stats.diferenciaMensual)}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${stats.diferenciaAnual < 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}><IconCalendar /></div>
                                            <span className="text-sm font-medium">Proyección Anual</span>
                                        </div>
                                        <span className={`font-bold ${stats.diferenciaAnual < 0 ? 'text-emerald-400' : 'text-red-400'}`}>{formatCurrency(stats.diferenciaAnual)}</span>
                                    </div>
                                    <div className="pt-4 border-t border-white/10 flex justify-between items-center">
                                        <span className="text-sm text-slate-400">Impacto Porcentual</span>
                                        <span className={`text-xl font-black ${stats.diferenciaPorcentual < 0 ? 'text-emerald-400' : 'text-red-400'}`}>{formatPercent(stats.diferenciaPorcentual)}</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="lg:col-span-2 space-y-4">
                        <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2"><IconTrophy className="w-5 h-5 text-yellow-500" /> Ranking de Ofertas</h3>
                        {listaVisual.map((oferta, index) => {
                             const isSelected = oferta === ofertaParaCalculos;
                             const estadoRow = oferta.estadoCotizacion || oferta.estadoRow || 'Cotizada';
                             const isInvalid = ['Solicitada', 'Bloqueada', 'No cotiza', 'Pendiente', 'Stand By'].includes(estadoRow);
                             const alicuota = parseFloat(oferta.alicuotaCotizada) || 0;
                            return (
                                <div key={index} onClick={() => !isInvalid && alicuota > 0 && setSelectedComparacion(oferta)} className={`relative p-5 rounded-xl border transition-all group ${isSelected ? 'bg-blue-50 border-blue-200 shadow-md ring-1 ring-blue-200' : isInvalid ? 'bg-gray-50 border-gray-100 opacity-80 cursor-default' : 'bg-white border-slate-200 hover:border-blue-300 hover:shadow-md cursor-pointer'}`}>
                                    {ganadora && oferta === ganadora && <div className="absolute -top-3 -right-3 bg-yellow-400 text-yellow-900 text-[10px] font-bold px-3 py-1 rounded-full shadow-sm z-10">✓ MEJOR PRECIO</div>}
                                    <div className="flex justify-between items-center mb-4">
                                        <div className="flex items-center gap-3">
                                            <div>
                                                <h4 className="font-bold text-slate-800 text-lg uppercase">{oferta.compania}</h4>
                                                {isInvalid ? <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-gray-200 text-gray-500 uppercase mt-1">{estadoRow}</span> : <p className="text-xs text-slate-500">Oferta válida procesada</p>}
                                            </div>
                                        </div>
                                        {(!isInvalid && alicuota > 0) ? (
                                            <div className="text-right">
                                                <p className="text-3xl font-black text-slate-800">{oferta.alicuotaCotizada.toString().replace('.', ',')}%</p>
                                                <p className="text-xs text-slate-500">Alícuota final</p>
                                            </div>
                                        ) : (
                                            <div className="text-right">
                                                <p className="text-lg font-black text-slate-500 uppercase">{estadoRow}</p>
                                                <p className="text-[10px] text-slate-400 font-bold uppercase">Estado de Oferta</p>
                                            </div>
                                        )}
                                    </div>
                                    {!isInvalid && alicuota > 0 && (
                                        <div className="grid grid-cols-4 gap-4 border-t border-slate-200/60 pt-4 text-left">
                                            <div><p className="text-xs text-slate-500 mb-1 uppercase font-bold text-[10px]">Mensual</p><p className="font-semibold text-slate-700">{formatCurrency((masaSalarial * (alicuota/100)))}</p></div>
                                            <div><p className="text-xs text-slate-500 mb-1 uppercase font-bold text-[10px]">p/ Cápita</p><p className="font-semibold text-slate-700">{formatCurrency((masaSalarial * (alicuota/100))/empleados)}</p></div>
                                            <div><p className="text-xs text-slate-500 mb-1 uppercase font-bold text-[10px]">Anual</p><p className="font-semibold text-slate-700">{formatCurrency((masaSalarial * (alicuota/100)) * 13)}</p></div>
                                            <div className="text-right flex items-end justify-end"><button className={`text-sm font-medium ${isSelected ? 'text-blue-600 font-bold' : 'text-slate-400 group-hover:text-blue-500'}`}>{isSelected ? 'Seleccionada' : 'Seleccionar'}</button></div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* MODAL DE CONVERSIÓN */}
            {showConfirmModal && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[2000] flex items-center justify-center p-4 animate-fade-in text-left">
                    <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full border border-orange-100 text-center scale-up">
                        <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4 text-orange-600">
                            <IconUserPlus />
                        </div>
                        <h3 className="text-xl font-black text-slate-800 uppercase tracking-tighter mb-2 text-center">¿Confirmar conversión?</h3>
                        <p className="text-slate-500 text-sm mb-8 text-center leading-relaxed">Dará de alta al prospecto como cliente y trasladará la cotización a su nueva carpeta.</p>
                        <div className="flex gap-3">
                            <button onClick={() => setShowConfirmModal(false)} className="flex-1 py-3 bg-slate-100 text-slate-600 font-bold rounded-xl uppercase text-xs tracking-widest hover:bg-slate-200 transition-all">Cancelar</button>
                            <button onClick={handleConvertToClient} className="flex-1 py-3 bg-orange-600 text-white font-black rounded-xl uppercase text-xs tracking-widest hover:bg-orange-700 shadow-lg shadow-orange-200 transition-all">Sí, convertir</button>
                        </div>
                    </div>
                </div>
            )}

            <ModalEdicionContrato 
                show={showContratoModal} 
                onClose={() => setShowContratoModal(false)} 
                clientData={clientDataForModal} 
                contrato={contratoPrefill} 
                onSuccess={handleContractSuccess} 
            />
            <ModalNotificacion show={notification.show} onClose={() => setNotification({ ...notification, show: false })} message={notification.message} type={notification.type} />

            <style>{`
                @keyframes bounce-in {
                    0% { opacity: 0; transform: translateY(-20px); }
                    60% { opacity: 1; transform: translateY(5px); }
                    100% { transform: translateY(0); }
                }
                .scale-up { animation: scaleUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) forwards; }
                @keyframes scaleUp { from { opacity: 0; transform: scale(0.9); } to { opacity: 1; transform: scale(1); } }
                .animate-bounce-in { animation: bounce-in 0.6s ease-out forwards; }
                .animate-fade-in { animation: fadeIn 0.3s ease-out; } 
                @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </div>
    );
};

export default DetalleCotizacionArt;